import { Button } from 'components/Button/Button'
import { useCallback, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { slsClient } from 'clients/slsClient'
import { useDispatch, useSelector } from 'react-redux'
import { getUserData } from 'redux/reducers/users'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Logger } from 'utils/logger'
import { useMountedState } from 'hooks/useMountedState'
import { createSchema, VerifyPasswordFormData } from 'models/forms/VerifyPasswordForm'
import { handleAccountLocked } from 'utils/handle-account-lockout'
import { useHistory } from 'react-router'
import { MyTextFieldPassword } from 'components/ReactHookForm/MyTextFieldPassword'
import { MyInternalLink } from 'components/MyLink'
import { medicareApiClient } from 'clients/medicareApiClient'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Activity, SubmissionType } from '@cms-max/medicare-api-sdk'
import { GenericError } from 'components/GenericError/GenericError'

export type ConfirmPasswordFormProps = {
  onSubmit: (password: string) => void,
  /**
   * If present, the cancel button will exist as an anchor tag
   */
  href?: string,
  /**
   * If present, the cancel button will exist. It defaults to a button, but will
   * be an anchor tag if the href prop is also present
   */
  onClose?: () => void
}

const trackPasswordVerificationResponse = (respStatus: 'Failure' | 'Success') => {
  window.globalHeaderEvents?.handleEvent({
    core: {
      activity: Activity.DownstreamServiceResponse,
      property: 'MAX',
      component: 'Account Settings',
      elementId: 'accountsettings_verifypassword_response',
      additionalData: {
        result: respStatus
      },
      submissionType: SubmissionType.Verify
    }
  })
}

type VerifyPasswordResponseState = 'initial' | 'loading' | 'locked' | 'success' | 'validation-error' | 'default-error'

/**
 * To be replaced by ConfirmPasswordFormMFA once MFA is turned on
 * These two components are very similar but are separated to simplify later work of removing the old logic.
 * @param props
 * @returns
 */
export const ConfirmPasswordForm = ({ onSubmit, onClose, href }: ConfirmPasswordFormProps) => {
  // Assumes this component is being used where launch darkly flags would already be loaded ie. Within LaunchDarklyDependentContent
  const flags = useFlags()

  const { t } = useTranslation('verifyPassword')
  const { user } = useSelector(getUserData)

  const [responseState, setResponseState] = useMountedState<VerifyPasswordResponseState>('initial')
  const [attemptsRemaining, setAttemptsRemaining] = useMountedState<string | undefined>(undefined)

  const alertContainerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const schema = useMemo(() => createSchema(t), [t])

  const methods = useForm<VerifyPasswordFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema)
  })

  const history = useHistory()
  const handleOnSubmit = useCallback(async (data:any) => {
    const handleError = () => {
      alertContainerRef.current?.focus()
    }

    if (user?.username) {
      setResponseState('loading')
      // Default to 'default-error' to reduce if/else logic in the try/catch below
      // If verify password returns false, or we get an unexpect error thrown, we use default-error
      let responseState: VerifyPasswordResponseState = 'default-error'
      try {
        if (flags.useUpdateAccountCookie) {
          if (await slsClient.updateAccountCookie(data.password)) {
            responseState = 'success'
          }
        } else {
          if (await slsClient.verifyPassword(user.username, data.password)) {
            responseState = 'success'
          }
        }
      } catch (error: unknown) {
        Logger.error(error)
        // Handle attempts remaining
        if (error instanceof Error) {
          if (error.message === 'user is locked') {
            responseState = 'locked'
          } else if (error.message === 'incorrect password' || error.message === 'missing password') {
            responseState = 'validation-error'
            const attemptsRemaining = await medicareApiClient.asyncGetPasswordAttemptsRemaining()
            setAttemptsRemaining(attemptsRemaining)
          }
        }
      }

      // Update the actual component state
      setResponseState(responseState)

      // Trigger side effects - activity tracking, focusing page elements, going to the update modal, etc.
      if (responseState === 'locked') {
        trackPasswordVerificationResponse('Failure')
        await handleAccountLocked(history, dispatch)
      } else if (responseState === 'validation-error' || responseState === 'default-error') {
        trackPasswordVerificationResponse('Failure')
        handleError()
      } else {
        trackPasswordVerificationResponse('Success')
        onSubmit(data.password)
      }
    } else {
      // This shouldn't happen because to get to account settings they need to be authenticated
      // Handled with the generic error
      setResponseState('default-error')
      handleError()
    }
  }, [
    dispatch,
    history,
    onSubmit,
    setResponseState,
    setAttemptsRemaining,
    user?.username,
    flags
  ])

  return (
    <>
      <FormProvider {...methods}>
        <div ref={alertContainerRef} tabIndex={-1}>
          {responseState === 'default-error' && (
            <GenericError />
          )}
          {responseState === 'validation-error' && (
            <Alert variation='error'>
              <p>{t('invalidPassword.doesNotMatchOurRecords')}</p>
              {flags.displaySlsAttemptsRemaining && attemptsRemaining && (
                <p>{t('invalidPassword.youHaveXMoreTries', { attemptsRemaining })}</p>
              )}
            </Alert>
          )}
        </div>
        <form onSubmit={methods.handleSubmit(handleOnSubmit)}>
          <div className="my-u-my--8">
            <MyTextFieldPassword
              name="password"
              label={t(`currentPassword`)}
              labelClassName="my-u-text-transform--default"
              defaultValue=""
            />
          </div>
          <div className="ds-c-dialog__actions">
            <div className="ds-l-form-row">
              <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                <Button
                  type="submit"
                  className="ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full my-u-whitespace-nowrap"
                  siteActivityProps={{
                    core: {
                      component: 'Account Settings',
                      elementId: 'accountsettings_verifypassword_click',
                      coreClickData: {
                        linkText: t('btn.verifyPassword')
                      }
                    }
                  }}
                >
                  {responseState === 'loading' && (
                    <MySpinner size="small" />
                  )}{' '}
                <span className="notranslate">{t('btn.verifyPassword')}</span>
                </Button>
              </div>
              {(href || onClose) && (
                <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                  {href
                    ? (
                        <MyInternalLink
                          className="ds-c-button my-u-py--6 my-u-width--full"
                          to={href}
                          onClick={onClose}
                        >
                          {t('btn.cancel')}
                        </MyInternalLink>
                      )
                    : (
                        <Button
                        className="ds-c-button my-u-py--6 my-u-width--full"
                        onClick={onClose}
                      >
                        {t('btn.cancel')}
                      </Button>
                      )
                  }
              </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  )
}
